import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { BarChart3, AlertCircle, ChevronDown, ChevronRight } from 'lucide-react';
import { reports, type DepartmentReportRow, type DirectorateReportRow, type ExpenseReportRow } from '../lib/api';
import { STATUS_META } from '../lib/expenseStatus';

function pct(spent: number, allocated: number): number {
  if (allocated <= 0) return 0;
  return Math.min(100, Math.round((spent / allocated) * 100));
}

function barColor(p: number): string {
  return p >= 100 ? 'bg-red-500' : p >= 80 ? 'bg-amber-500' : 'bg-green-500';
}

export default function Reports() {
  const [params] = useSearchParams();
  const businessId = params.get('business_id') || undefined;
  const [year, setYear] = useState(new Date().getFullYear());
  const [departments, setDepartments] = useState<DepartmentReportRow[]>([]);
  const [directorates, setDirectorates] = useState<DirectorateReportRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [expandedDept, setExpandedDept] = useState<string | null>(null);
  const [deptExpenses, setDeptExpenses] = useState<Record<string, ExpenseReportRow[]>>({});
  const [deptLoading, setDeptLoading] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [d, dir] = await Promise.all([
        reports.departments(year, businessId),
        reports.directorates(year, businessId),
      ]);
      setDepartments(d.departments);
      setDirectorates(dir.directorates);
      setExpandedDept(null);
      setDeptExpenses({});
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [year, businessId]);

  useEffect(() => { load(); }, [load]);

  const toggleDepartment = async (departmentId: string) => {
    if (expandedDept === departmentId) {
      setExpandedDept(null);
      return;
    }
    setExpandedDept(departmentId);
    if (!deptExpenses[departmentId]) {
      setDeptLoading(departmentId);
      try {
        const { expenses } = await reports.expenses({ department_id: departmentId, year }, businessId);
        setDeptExpenses(prev => ({ ...prev, [departmentId]: expenses }));
      } catch (err: any) {
        setError(err.message);
      } finally {
        setDeptLoading(null);
      }
    }
  };

  const totalAllocated = directorates.reduce((s, d) => s + d.allocated, 0);
  const totalSpent = directorates.reduce((s, d) => s + d.spent, 0);

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BarChart3 className="w-6 h-6 text-green-600" /> Reports
          </h1>
          <p className="text-gray-500 text-sm mt-0.5">Budget vs. spend by directorate and department</p>
        </div>
        <input type="number" className="input-field w-28" value={year} onChange={e => setYear(Number(e.target.value))} />
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2 text-sm">
          <AlertCircle className="w-4 h-4" /> {error}
        </div>
      )}

      {loading ? (
        <div className="p-8 text-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto" /></div>
      ) : (
        <>
          <div className="card space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="font-semibold text-gray-900">Total ({year})</span>
              <span className="tabular-nums text-gray-700">{totalSpent.toFixed(2)} / {totalAllocated.toFixed(2)}</span>
            </div>
            <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden">
              <div className={`h-full rounded-full ${barColor(pct(totalSpent, totalAllocated))}`} style={{ width: `${pct(totalSpent, totalAllocated)}%` }} />
            </div>
          </div>

          <div className="card space-y-4">
            <h2 className="font-semibold text-gray-900">By Directorate</h2>
            {directorates.length === 0 ? (
              <p className="text-center text-gray-400 text-sm py-4">No directorates configured</p>
            ) : (
              <div className="space-y-4">
                {directorates.map(d => (
                  <div key={d.directorate_id} className="space-y-1">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium text-gray-700">{d.directorate_name}</span>
                      <span className="tabular-nums text-gray-500">{d.spent.toFixed(2)} / {d.allocated.toFixed(2)}</span>
                    </div>
                    <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className={`h-full rounded-full ${barColor(pct(d.spent, d.allocated))}`} style={{ width: `${pct(d.spent, d.allocated)}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="card overflow-hidden p-0">
            <h2 className="font-semibold text-gray-900 px-6 pt-6 pb-2">By Department</h2>
            <p className="text-xs text-gray-400 px-6 pb-2">Click a department to see individual expenses under its Head of Department</p>
            {departments.length === 0 ? (
              <p className="text-center text-gray-400 text-sm py-8">No departments configured</p>
            ) : (
              <div className="divide-y divide-gray-50">
                {departments.map(d => {
                  const isOpen = expandedDept === d.department_id;
                  return (
                    <div key={d.department_id}>
                      <button
                        onClick={() => toggleDepartment(d.department_id)}
                        className="w-full flex items-center gap-3 px-6 py-3 text-sm text-left hover:bg-gray-50 transition-colors"
                      >
                        {isOpen ? <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" /> : <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-900">{d.department_name}</div>
                          <div className="text-xs text-gray-400">
                            {d.directorate_name}{d.head_name ? ` · Head: ${d.head_name}` : ' · No head assigned'}
                          </div>
                        </div>
                        <span className="tabular-nums text-gray-700 flex-shrink-0">{d.spent.toFixed(2)} / {d.allocated.toFixed(2)}</span>
                      </button>

                      {isOpen && (
                        <div className="bg-gray-50 px-6 py-3">
                          {deptLoading === d.department_id ? (
                            <div className="text-center py-4"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600 mx-auto" /></div>
                          ) : (deptExpenses[d.department_id]?.length ?? 0) === 0 ? (
                            <p className="text-center text-gray-400 text-sm py-3">No expenses in {year}</p>
                          ) : (
                            <div className="space-y-2">
                              {deptExpenses[d.department_id].map(e => {
                                const meta = STATUS_META[e.status];
                                return (
                                  <div key={e.id} className="flex items-center gap-3 text-sm bg-white rounded-lg border border-gray-100 px-3 py-2">
                                    <div className="flex-1 min-w-0">
                                      <div className="font-medium text-gray-900 truncate">{e.submitter_name}</div>
                                      <div className="text-xs text-gray-400 truncate">{e.description || 'No description'} &bull; {e.expense_date}</div>
                                    </div>
                                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full flex-shrink-0 ${meta.className}`}>{meta.label}</span>
                                    <span className="tabular-nums text-gray-900 font-semibold flex-shrink-0">{e.amount.toFixed(2)}</span>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
